import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Building2,
  Plus,
  Search,
  Building,
  User,
  ChevronRight,
  MapPin,
  Home,
  Loader2,
  Trash2,
  Briefcase
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

const Properties = () => {
  const { token, isManager } = useAuth();
  const [clientsData, setClientsData] = useState([]);
  const [owners, setOwners] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteProperty, setDeleteProperty] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("all");

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    owner_type: "",
    owner_id: "",
    property_type: "residential",
    units: 1,
    has_basement: false,
    description: ""
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [clientsRes, ownersRes] = await Promise.all([
        axios.get(`${API}/properties/by-client`, { headers }),
        axios.get(`${API}/owners`, { headers })
      ]);
      setClientsData(clientsRes.data);
      setOwners(ownersRes.data);
    } catch (error) {
      toast.error("Failed to load properties");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  // Flatten all owners with their properties for display
  const getAllOwners = () => {
    const allOwners = [];
    clientsData.forEach(client => {
      (client.owners || []).forEach(owner => {
        allOwners.push({
          ...owner,
          client_name: client.name,
          client_id: client.id
        });
      });
    });
    return allOwners;
  };

  // Filter owners by type for dropdown
  const filteredOwners = formData.owner_type
    ? owners.filter(o => o.owner_type === formData.owner_type)
    : [];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.address || !formData.owner_id) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/properties`, {
        name: formData.name,
        address: formData.address,
        owner_id: formData.owner_id,
        property_type: formData.property_type,
        units: formData.units,
        has_basement: formData.has_basement,
        description: formData.description
      }, { headers });
      toast.success("Property created successfully");
      setDialogOpen(false);
      setFormData({
        name: "",
        address: "",
        owner_type: "",
        owner_id: "",
        property_type: "residential",
        units: 1,
        has_basement: false,
        description: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create property");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/properties/${deleteProperty.id}`, { headers });
      toast.success("Property deleted");
      setDeleteProperty(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete property");
    }
  };

  const getOwnerTypeColor = (type) => {
    switch (type) {
      case "llc": return { bg: "bg-purple-100", text: "text-purple-700", icon: "text-purple-600" };
      case "corp": return { bg: "bg-blue-100", text: "text-blue-700", icon: "text-blue-600" };
      case "trust": return { bg: "bg-amber-100", text: "text-amber-700", icon: "text-amber-600" };
      default: return { bg: "bg-green-100", text: "text-green-700", icon: "text-green-600" };
    }
  };

  const getOwnerIcon = (type) => {
    switch (type) {
      case "llc": return <Briefcase className="w-5 h-5" />;
      case "corp": return <Building className="w-5 h-5" />;
      case "trust": return <Building2 className="w-5 h-5" />;
      default: return <User className="w-5 h-5" />;
    }
  };

  // Get all owners with their properties, filtered by search
  const getFilteredOwners = (typeFilter = null) => {
    const allOwners = getAllOwners();
    
    return allOwners
      .filter(owner => {
        // Type filter
        if (typeFilter && typeFilter !== "all" && owner.owner_type !== typeFilter) {
          return false;
        }
        
        // Search filter
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          const matchesOwner = owner.name.toLowerCase().includes(query);
          const matchesProperties = (owner.properties || []).some(p => 
            p.name.toLowerCase().includes(query) || 
            p.address.toLowerCase().includes(query)
          );
          return matchesOwner || matchesProperties;
        }
        
        return true;
      })
      .map(owner => {
        // Filter properties within owner by search
        if (searchQuery) {
          const query = searchQuery.toLowerCase();
          return {
            ...owner,
            properties: (owner.properties || []).filter(p =>
              p.name.toLowerCase().includes(query) ||
              p.address.toLowerCase().includes(query) ||
              owner.name.toLowerCase().includes(query)
            )
          };
        }
        return owner;
      });
  };

  const allOwners = getFilteredOwners("all");
  const totalProperties = allOwners.reduce((sum, o) => sum + (o.properties?.length || 0), 0);

  // Count by type
  const llcCount = getFilteredOwners("llc").reduce((sum, o) => sum + (o.properties?.length || 0), 0);
  const personalCount = getFilteredOwners("personal").reduce((sum, o) => sum + (o.properties?.length || 0), 0);
  const trustCount = getFilteredOwners("trust").reduce((sum, o) => sum + (o.properties?.length || 0), 0);
  const corpCount = getFilteredOwners("corp").reduce((sum, o) => sum + (o.properties?.length || 0), 0);

  const renderOwnerSection = (ownersData) => (
    <div className="space-y-4">
      {ownersData.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-slate-400" />
          </div>
          <p className="text-slate-500">No properties found</p>
        </div>
      ) : (
        ownersData.map((owner) => {
          const colors = getOwnerTypeColor(owner.owner_type);
          return (
            <Card key={owner.id} className="overflow-hidden" data-testid={`owner-properties-${owner.id}`}>
              <CardHeader className="bg-slate-50 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.bg}`}>
                      <span className={colors.icon}>{getOwnerIcon(owner.owner_type)}</span>
                    </div>
                    <div>
                      <CardTitle className="text-base">{owner.name}</CardTitle>
                      <p className="text-sm text-slate-500">
                        {owner.property_count || 0} {(owner.property_count || 0) === 1 ? "property" : "properties"}
                        {owner.client_name && <span className="text-slate-400"> • {owner.client_name}</span>}
                      </p>
                    </div>
                  </div>
                  <Badge className={`${colors.bg} ${colors.text}`}>
                    {owner.owner_type?.toUpperCase() || "PERSONAL"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {(!owner.properties || owner.properties.length === 0) ? (
                  <div className="p-6 text-center text-slate-500">
                    No properties under this {owner.owner_type?.toUpperCase() || "owner"}
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {owner.properties.map((property) => (
                      <div
                        key={property.id}
                        className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                        data-testid={`property-${property.id}`}
                      >
                        <Link
                          to={`/properties/${property.id}`}
                          className="flex items-center gap-4 flex-1"
                        >
                          <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                            <Home className="w-6 h-6 text-slate-400" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{property.name}</p>
                            <div className="flex items-center gap-1 text-sm text-slate-500">
                              <MapPin className="w-3 h-3" />
                              {property.address}
                            </div>
                          </div>
                        </Link>
                        <div className="flex items-center gap-4">
                          <div className="text-right hidden sm:block">
                            <p className="text-sm font-medium text-slate-700">
                              {property.units} {property.units === 1 ? "unit" : "units"}
                            </p>
                            {property.tenant_count > 0 && (
                              <p className="text-xs text-slate-500">{property.tenant_count} tenants</p>
                            )}
                          </div>
                          {isManager && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.preventDefault(); setDeleteProperty(property); }}
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-property-${property.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                          <Link to={`/properties/${property.id}`}>
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="animate-fade-in">
        <div className="page-header">
          <div className="skeleton h-8 w-48" />
        </div>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="skeleton h-32 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="properties-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Properties</h1>
          <p className="text-slate-500 mt-1">
            {totalProperties} {totalProperties === 1 ? "property" : "properties"}
          </p>
        </div>
        {isManager && (
          <Button onClick={() => setDialogOpen(true)} className="bg-green-700 hover:bg-green-800" data-testid="add-property-button">
            <Plus className="w-4 h-4 mr-2" />
            Add Property
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search properties..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="property-search-input"
          />
        </div>
      </div>

      {/* Tabs by Owner Type */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList>
          <TabsTrigger value="all" data-testid="tab-all">All ({totalProperties})</TabsTrigger>
          <TabsTrigger value="llc" data-testid="tab-llc">LLC ({llcCount})</TabsTrigger>
          <TabsTrigger value="personal" data-testid="tab-personal">Personal ({personalCount})</TabsTrigger>
          <TabsTrigger value="trust" data-testid="tab-trust">Trust ({trustCount})</TabsTrigger>
          <TabsTrigger value="corp" data-testid="tab-corp">Corp ({corpCount})</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {renderOwnerSection(getFilteredOwners("all"))}
        </TabsContent>

        <TabsContent value="llc">
          {renderOwnerSection(getFilteredOwners("llc"))}
        </TabsContent>

        <TabsContent value="personal">
          {renderOwnerSection(getFilteredOwners("personal"))}
        </TabsContent>

        <TabsContent value="trust">
          {renderOwnerSection(getFilteredOwners("trust"))}
        </TabsContent>

        <TabsContent value="corp">
          {renderOwnerSection(getFilteredOwners("corp"))}
        </TabsContent>
      </Tabs>

      {/* Add Property Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Property</DialogTitle>
            <DialogDescription>
              Add a new property to your portfolio. Select the owner type and entity first.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Step 1: Select Owner Type */}
            <div className="space-y-2">
              <Label>Owner Type *</Label>
              <Select
                value={formData.owner_type}
                onValueChange={(value) => setFormData({ ...formData, owner_type: value, owner_id: "" })}
              >
                <SelectTrigger data-testid="owner-type-select">
                  <SelectValue placeholder="Select LLC, Personal, Trust, or Corp" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="llc">LLC</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="trust">Trust</SelectItem>
                  <SelectItem value="corp">Corporation</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Step 2: Select specific owner of that type */}
            {formData.owner_type && (
              <div className="space-y-2">
                <Label>Select {formData.owner_type.toUpperCase()} *</Label>
                {filteredOwners.length === 0 ? (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      No {formData.owner_type.toUpperCase()} owners found. 
                      <Link to="/clients" className="ml-1 text-green-700 hover:underline font-medium">
                        Add one first
                      </Link>
                    </p>
                  </div>
                ) : (
                  <Select
                    value={formData.owner_id}
                    onValueChange={(value) => setFormData({ ...formData, owner_id: value })}
                  >
                    <SelectTrigger data-testid="owner-select">
                      <SelectValue placeholder={`Select ${formData.owner_type.toUpperCase()}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredOwners.map((owner) => (
                        <SelectItem key={owner.id} value={owner.id}>
                          {owner.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Property Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Maple Street Apartments"
                data-testid="property-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Address *</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, City, State"
                data-testid="property-address-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Property Type</Label>
                <Select
                  value={formData.property_type}
                  onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="residential">Residential</SelectItem>
                    <SelectItem value="commercial">Commercial</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Number of Units</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.units}
                  onChange={(e) => setFormData({ ...formData, units: parseInt(e.target.value) || 1 })}
                  data-testid="property-units-input"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="has_basement"
                checked={formData.has_basement}
                onChange={(e) => setFormData({ ...formData, has_basement: e.target.checked })}
                className="rounded border-slate-300"
              />
              <Label htmlFor="has_basement" className="cursor-pointer">Has Basement</Label>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Additional details about the property..."
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-green-700 hover:bg-green-800" 
                disabled={saving || !formData.owner_type || !formData.owner_id} 
                data-testid="save-property-button"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Property"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteProperty} onOpenChange={() => setDeleteProperty(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Property?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteProperty?.name}</strong>? 
              This will also remove all associated tenants and leases. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Properties;
